/** Angular imports */
import { afterNextRender, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

/** Component imports */
import { Button, StepClickEvent, Stepper } from '@ntv360/component-pantry';
import { FillInformationComponent } from './steps/fill-information/fill-information.component';
import { SelectAssetsComponent } from './steps/select-assets/select-assets.component';
import { SettingsComponent } from './steps/settings/settings.component';

/** Core imports */
import { PlaylistDto } from '@core/dto';
import { AuthService } from '@core/services/auth/auth.service';
import { MainLayoutStore } from '@layouts/main-layout';

/** Local imports */
import { PlaylistsService } from '@core/services/playlists/playlists.service';
import { PLAYLIST_STEPS } from '@features/playlists/constants';
import { CreatePlaylistStepsService } from '@features/playlists/services/steps/steps.service';
import { ConfirmationModalComponent, ConfirmationModalType } from '@shared';
import { getContentType, withLoading } from '@shared/utils';

import { PlaylistDealerAssignment } from '@features/playlists/interfaces/playlist-dealer-assignment.interface';
import { UiDealersAutocomplete } from '@features/users';
import { PlaylistSettingsContentComponent } from '../ui/playlist-settings-content/playlist-settings-content.component';

/** Step components array */
const stepComponents = [FillInformationComponent, SelectAssetsComponent, SettingsComponent];
@Component({
    selector: 'app-create-playlist',
    imports: [Stepper, Button, ConfirmationModalComponent, ...stepComponents],
    templateUrl: './create-playlist.component.html',
    styleUrl: './create-playlist.component.scss',
})
export class CreatePlaylistComponent {
    /** Built-in destroy reference to automatically clean up on destroy */
    private readonly destroyRef = inject(DestroyRef);

    /** Service managing the steps of the create playlist flow */
    private stepsService = inject(CreatePlaylistStepsService);

    /** Service for playlist operations */
    private playlistService = inject(PlaylistsService);

    /** Auth service for current user information */
    private authService = inject(AuthService);

    /** Router service for navigation */
    private router = inject(Router);

    /** Create playlist stepper constant */
    protected playlistSteps = PLAYLIST_STEPS;

    protected isSubmitting = signal(false);
    protected isPlaylistCreated = signal(false);
    protected showConfirmationModal = signal(false);

    /** Modal configuration both for success and error modal for create playlist submission */
    protected modalConfig = signal<{
        type: ConfirmationModalType;
        title: string;
        message: string;
    }>({
        type: 'success',
        title: '',
        message: '',
    });

    /** Holds the current step id */
    protected currentStepId = this.stepsService.currentStepId;

    /** Checks if the current step is valid */
    protected isStepValid = this.stepsService.isStepValid;

    constructor() {
        afterNextRender(() => {
            MainLayoutStore.setBreadcrumb('Create Playlist');
        });

        this.destroyRef.onDestroy(() => {
            MainLayoutStore.clearBreadcrumb();
        });
    }

    /**
     * Handles stepper click navigation with validation rules:
     * - Backwards navigation is always allowed
     * - Forward navigation is only allowed if all steps up to the target are valid
     */
    protected handleStepClick(event: StepClickEvent): void {
        const targetIndex = event.index;
        const currentIndex = this.currentStepId();

        // Always allow backward navigation
        if (targetIndex < currentIndex) {
            this.stepsService.goToStep(targetIndex);
            return;
        }

        // For forward navigation, validate all steps between current and target
        for (let i = currentIndex; i < targetIndex; i++) {
            if (!this.stepsService.isStepValidById(i)) {
                return;
            }
        }

        this.stepsService.goToStep(targetIndex);
    }

    /** Move to the previous create playlist step */
    protected prevStep(): void {
        this.stepsService.prevStep();
    }

    /** Move to the next create playlist step */
    protected nextStep(): void {
        const currentStep = this.currentStepId();
        const totalSteps = this.playlistSteps.length;

        // If it's the last step, call the create playlist service
        if (currentStep === totalSteps - 1) {
            this.createPlaylist();
            return;
        }

        this.stepsService.nextStep();
    }

    /**
     * Handles the final playlist creation
     */
    private createPlaylist(): void {
        const draft = this.stepsService.playlistDraft();
        const user = this.authService.user();

        if (!user) return;

        const settings = draft.settings;

        const startDatetime = settings?.schedule?.dateRange.start
            ? new Date(settings.schedule.dateRange.start).toISOString()
            : PlaylistSettingsContentComponent.defaultStartDatetime;

        const endDatetime = settings?.schedule?.dateRange.end
            ? new Date(settings.schedule.dateRange.end).toISOString()
            : PlaylistSettingsContentComponent.defaultEndDatetime;

        const defaults = PlaylistSettingsContentComponent.DEFAULT_VALUES;

        const daysOfWeekArr = settings?.schedule?.daysToPlay.playAllDays
            ? defaults.DAYS_OF_WEEK
            : settings?.schedule?.daysToPlay.selectedDays.map((d) => defaults.DAY_MAP[d]) ||
              defaults.DAYS_OF_WEEK;

        const draftDealer = draft.fillInformation.assignToDealer as PlaylistDealerAssignment;
        const ownerId =
            typeof draft.fillInformation.assignToDealer === 'string'
                ? draft.fillInformation.assignToDealer
                : (draftDealer.user?.userId ?? draftDealer.ownerId ?? draftDealer.value ?? '');

        const payload: PlaylistDto = {
            playlistName: draft.fillInformation.playlistName,
            description: draft.fillInformation.playlistDescription,
            ownerId,
            metadata: JSON.stringify({ visibility: 'internal', tags: [] }),
            contents: draft.selectAssets.addedPlaylistContents.map((content) => ({
                contentId: content.id,
                contentType: 'media',
                duration:
                    getContentType(content.type) === 'video'
                        ? Number(content.duration || 20)
                        : (Number(draft.settings?.duration) ?? 20),
                schedules: [
                    {
                        startDatetime,
                        endDatetime,
                        startTime: PlaylistSettingsContentComponent.formatTime(
                            settings?.schedule?.playTime.start ?? null,
                            defaults.START_TIME,
                        ),
                        endTime: PlaylistSettingsContentComponent.formatTime(
                            settings?.schedule?.playTime.end ?? null,
                            defaults.END_TIME,
                        ),
                        daysOfWeek: JSON.stringify(daysOfWeekArr),
                    },
                ],
            })),
            isFullscreen: defaults.IS_FULLSCREEN,
            createdBy: user.data.userId,
        };

        this.playlistService
            .createPlaylist(payload)
            .pipe(withLoading(this.isSubmitting), takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ message }) => {
                    this.isPlaylistCreated.set(true);
                    this.modalConfig.set({
                        type: 'success',
                        title: 'Playlist Created Successfully',
                        message: message || 'Your playlist has been created and is ready to use.',
                    });
                    this.showConfirmationModal.set(true);
                },
                error: (error) => {
                    this.isPlaylistCreated.set(false);
                    this.modalConfig.set({
                        type: 'error',
                        title: 'Playlist Creation Failed',
                        message:
                            'An error occurred while creating your playlist. Please try again.',
                    });
                    this.showConfirmationModal.set(true);
                    console.error('Error creating playlist:', error);
                },
            });
    }

    /**
     * Handles closing the confirmation modal
     */
    protected onModalClose(): void {
        this.showConfirmationModal.set(false);

        if (this.isPlaylistCreated()) {
            this.router.navigate(['/playlists']);
        }
    }
}
